/**
 * In-app plan changes: the changes a coach can make to her own NOVA
 * subscription without Stripe's billing portal, which of them her subscription
 * allows, and what each plan card on Plans and Pricing offers her.
 *
 * Pure and client-safe. The server applies {@link resolvePlanChange} to the
 * live subscription before any preview or charge, and the billing page applies
 * {@link planCardCta} to the same facts, so a card only offers a change the
 * server will make.
 *
 * A coach who pays for the Marketing Suite on its own, with no NOVA plan, can
 * switch it to NOVA Pro, which contains it. The switch replaces the Suite on
 * her one subscription, so she never pays for the Suite twice.
 *
 * A paid coach can also move down one plan, Pro+ to Pro or Pro to Core, on
 * the same term. The cancel flow offers it to a coach who finds NOVA too
 * expensive.
 *
 * Everything else stays in the portal: other downgrades, yearly to monthly,
 * and any change to a subscription that is past due, set to end, paused, or
 * already waiting on another change.
 *
 * A lapsed coach has no subscription to change. The NOVA Core card offers her
 * free win-back week while she can still take it, and every other card keeps
 * its checkout.
 *
 * A coach with free access from staff has no subscription to change either.
 * Her NOVA Core card says it is her plan, and the higher plans keep their
 * normal paid checkout.
 *
 * NOVA Pro+ is an Accelerator graduate's plan. Its card appears only for a
 * graduate, and {@link resolvePlanChange} refuses any change onto it for
 * anyone else, whatever the request.
 *
 * The page has one purple button, her next step ({@link nextStep}). Each card
 * reports what its button does ({@link planCardAction}), and the next step is
 * the first of those that moves her forward.
 *
 * Aliases: plan change, upgrade to pro, upgrade to pro+, switch to yearly,
 * end trial early, start paid plan, plan card cta, plans and pricing cta,
 * win-back card, switch suite to nova pro, downgrade, next plan down,
 * next step, one purple button, plan card action
 */

import type { NovaAccessState } from "~/lib/functions/nova-billing";
import type { CoachLifecycle } from "~/lib/shared/coach-account";
import { formatStripeAmount } from "~/lib/shared/currency";
import {
  conflictingPlanKeys,
  isSuitePlanLookupKey,
  type BillingTerm,
  type PlanKey,
} from "~/lib/shared/plan-catalogue";

/**
 * - `start_paid`: end the trial today and start paying for her plan now.
 * - `upgrade_pro`: move from NOVA Core to NOVA Pro. From the trial, this also
 *   ends the trial today.
 * - `upgrade_pro_plus`: move from NOVA Core or NOVA Pro to NOVA Pro+, for an
 *   Accelerator graduate. From the trial, this also ends the trial today:
 *   Pro+ has no trial.
 * - `switch_yearly`: keep her plan and pay yearly instead of monthly.
 * - `suite_to_pro`: replace her standalone Marketing Suite with NOVA Pro, on
 *   the same subscription. NOVA Pro has the Suite in it, and no trial.
 * - `downgrade`: move a paid plan to the next plan down ({@link
 *   nextPlanDown}) on the same term.
 */
export const PLAN_CHANGE_MODES = [
  "start_paid",
  "upgrade_pro",
  "upgrade_pro_plus",
  "switch_yearly",
  "suite_to_pro",
  "downgrade",
] as const;
export type PlanChangeMode = (typeof PLAN_CHANGE_MODES)[number];

/** The plans a coach can change to in the app. NOVA Pro+ only for a graduate. */
export type ChangeablePlan = "nova" | "nova_pro" | "nova_pro_plus";

/** A plan the app sells, and the term it bills on. */
export interface PlanTerm {
  plan: ChangeablePlan;
  term: BillingTerm;
}

/** What Plans and Pricing offers a coach whose NOVA access has lapsed. */
export interface WinbackPlans {
  /** Her last NOVA plan and term, badged on its card. Null when Stripe has
   *  none, or her last was a plan this page does not sell. */
  lastPlan: PlanTerm | null;
  /** The plan and term her free 7-day win-back trial applies to, while she
   *  can still take it: NOVA Core, on her last term, or monthly when there is
   *  none. Never NOVA Pro, even when she was last on it. */
  trial: PlanTerm | null;
}

/**
 * The NOVA Pro card's button for a coach who pays for NOVA Core, or for the
 * Marketing Suite on its own.
 */
export const SWITCH_TO_PRO_CTA = "Switch to NOVA Pro";

/** What every way back that carries her free win-back week says. */
export const WINBACK_TRIAL_CTA = "Try NOVA Core free for 7 days";

/** What a plan card says of the plan she pays for. */
export const OWN_PLAN_LABEL = "Your plan";

/** The NOVA Core card while she has free access from staff. */
export const FREE_ACCESS_PLAN_CTA = "Current plan · free access";

/** Why a change was refused. Each has one message, in `PLAN_CHANGE_REFUSAL_COPY`. */
export const PLAN_CHANGE_REFUSALS = [
  "no_subscription",
  "payment_issue",
  "cancel_pending",
  "paused",
  "change_scheduled",
  "no_payment_method",
  "not_available",
  "plan_overlap",
  "price_unavailable",
  "quote_changed",
  "quote_expired",
  "payment_failed",
  "promo_code_invalid",
] as const;
export type PlanChangeRefusal = (typeof PLAN_CHANGE_REFUSALS)[number];

/** What she is told when a change is refused. Nothing was charged in any case. */
export const PLAN_CHANGE_REFUSAL_COPY: Record<PlanChangeRefusal, string> = {
  no_subscription:
    "You don’t have a NOVA plan to change. Choose one on Plans and Pricing.",
  payment_issue:
    "Your last payment didn’t go through. Update your card in billing before changing your plan.",
  cancel_pending:
    "Your plan is set to end. Choose Keep NOVA in Settings › Billing before changing it.",
  paused:
    "Payments on your plan are paused. Resume them in billing before changing your plan.",
  change_scheduled:
    "Your plan already has a change waiting. Manage your plan in billing instead.",
  no_payment_method:
    "There’s no card on file to charge. Add one in billing, then come back to make this change.",
  not_available:
    "This change isn’t available for your plan. You can manage your plan in billing.",
  plan_overlap:
    "This plan includes a plan you already pay for on its own, such as the Marketing Suite. Upgrading here would charge you for it twice, so change your plans in billing.",
  price_unavailable:
    "This plan’s price isn’t available right now. Nothing was charged. Try again later.",
  quote_changed:
    "The amount changed since you opened this page. Nothing was charged. Review the new amount below.",
  quote_expired:
    "This amount was worked out too long ago. Nothing was charged. Review the new amount below.",
  payment_failed:
    "Your bank didn’t approve this payment, so nothing changed. Check your card in billing, or make this change there.",
  promo_code_invalid:
    "That promo code can’t be used for this change. Check the code, or continue without it.",
};

/**
 * A promo code applied to a plan change, as the confirm page states it. Only
 * the coupon's terms travel to the client, never its id.
 */
export interface PlanChangePromotion {
  /** The code as she typed it, in Stripe's spelling. */
  code: string;
  /** A fixed amount off, in the currency's smallest unit, or null for a percentage. */
  amountOffCents: number | null;
  percentOff: number | null;
  currency: string | null;
  duration: "once" | "repeating" | "forever";
  durationInMonths: number | null;
}

/** What a promo code takes off, in words: "$50.00 off each month for 12 months". */
export function describePromotion(promotion: PlanChangePromotion): string {
  const off =
    promotion.amountOffCents !== null && promotion.currency !== null
      ? formatStripeAmount(promotion.amountOffCents, promotion.currency)
      : `${promotion.percentOff ?? 0}%`;
  switch (promotion.duration) {
    case "once":
      return `${off} off your next payment`;
    case "forever":
      return `${off} off every payment`;
    case "repeating": {
      const months = promotion.durationInMonths ?? 1;
      return `${off} off each payment for ${months} ${months === 1 ? "month" : "months"}`;
    }
    default: {
      const exhaustive: never = promotion.duration;
      throw new Error(`Unknown coupon duration: ${String(exhaustive)}`);
    }
  }
}

/** The live subscription facts a change depends on. */
export interface PlanChangeSubscription {
  /** Stripe's subscription status. */
  status: string;
  /** The plan it bills, or null when the product names none of ours. A NOVA
   *  plan, or the standalone Marketing Suite for `suite_to_pro`. */
  plan: PlanKey | null;
  /** The term its price bills on, or null for an interval we do not sell. */
  term: BillingTerm | null;
  /** Set to end, at the period end or on a set date. */
  endScheduled: boolean;
  /** Payment collection is paused. A change would still take effect, and
   *  its invoice would not be paid. */
  paused: boolean;
  /** A subscription schedule, or an earlier update that waits for payment,
   *  will change it. Either could undo or overwrite a change made here. */
  changeScheduled: boolean;
}

/** What a change moves the subscription to. */
export interface PlanChangeTarget {
  plan: ChangeablePlan;
  term: BillingTerm;
  /** The trial ends now and the paid cycle starts today. */
  endsTrial: boolean;
}

export type PlanChangeDecision =
  | { ok: true; target: PlanChangeTarget }
  | { ok: false; reason: PlanChangeRefusal };

const PLAN_RANK: Record<PlanKey, number> = {
  marketing_suite: 0,
  nova: 1,
  nova_pro: 2,
  nova_pro_plus: 3,
};

export function isChangeablePlan(plan: PlanKey): plan is ChangeablePlan {
  return plan === "nova" || plan === "nova_pro" || plan === "nova_pro_plus";
}

/**
 * The plan one step below `plan`, or null for NOVA Core and the Suite. Pro+
 * to Pro keeps the Marketing Suite; Pro to Core removes it.
 */
export function nextPlanDown(plan: PlanKey): ChangeablePlan | null {
  switch (plan) {
    case "nova_pro_plus":
      return "nova_pro";
    case "nova_pro":
      return "nova";
    case "nova":
    case "marketing_suite":
      return null;
    default: {
      const exhaustive: never = plan;
      throw new Error(`Unknown plan: ${String(exhaustive)}`);
    }
  }
}

/** The same term, or up from monthly to yearly. Yearly to monthly is the portal's. */
export function canMoveTerm(from: BillingTerm, to: BillingTerm): boolean {
  return from === to || (from === "monthly" && to === "yearly");
}

const refuse = (reason: PlanChangeRefusal): PlanChangeDecision => ({
  ok: false,
  reason,
});

/**
 * Why this subscription takes no in-app change at all, whatever the request,
 * or null when it can take one. The plan cards read the same answer, so a
 * subscription the app cannot change offers no change.
 */
export function subscriptionRefusal(
  subscription: PlanChangeSubscription,
): PlanChangeRefusal | null {
  const { status } = subscription;
  if (status === "past_due" || status === "unpaid" || status === "incomplete")
    return "payment_issue";
  if (status !== "trialing" && status !== "active") return "no_subscription";
  if (subscription.endScheduled) return "cancel_pending";
  if (subscription.paused) return "paused";
  if (subscription.changeScheduled) return "change_scheduled";
  return null;
}

/**
 * Whether `request` is a change this subscription allows, and what it changes
 * to. The one rule for both the preview and the charge.
 *
 * `proPlusEligible` is her Accelerator graduation, read from her row on the
 * server. Without it no change ends on NOVA Pro+: not an upgrade onto it, and
 * not a change of how a Pro+ plan she was put on by hand is paid. Those stay
 * in the billing portal.
 */
export function resolvePlanChange(
  subscription: PlanChangeSubscription,
  request: { mode: PlanChangeMode; term: BillingTerm },
  { proPlusEligible }: { proPlusEligible: boolean },
): PlanChangeDecision {
  const blocked = subscriptionRefusal(subscription);
  if (blocked) return refuse(blocked);
  const { status, plan, term } = subscription;
  if (plan === null || term === null) return refuse("not_available");
  if (!canMoveTerm(term, request.term)) return refuse("not_available");

  const target = changeTarget(plan, term, status === "trialing", request);
  if (!target) return refuse("not_available");
  if (target.plan === "nova_pro_plus" && !proPlusEligible)
    return refuse("not_available");
  return { ok: true, target };
}

/** What `request` moves this plan to, or null when it is not a change of it. */
function changeTarget(
  plan: PlanKey,
  term: BillingTerm,
  trialing: boolean,
  request: { mode: PlanChangeMode; term: BillingTerm },
): PlanChangeTarget | null {
  switch (request.mode) {
    case "start_paid":
      return trialing && isChangeablePlan(plan)
        ? { plan, term: request.term, endsTrial: true }
        : null;
    case "upgrade_pro":
      return plan === "nova"
        ? { plan: "nova_pro", term: request.term, endsTrial: trialing }
        : null;
    case "upgrade_pro_plus":
      return plan === "nova" || plan === "nova_pro"
        ? { plan: "nova_pro_plus", term: request.term, endsTrial: trialing }
        : null;
    case "switch_yearly":
      return !trialing &&
        isChangeablePlan(plan) &&
        term === "monthly" &&
        request.term === "yearly"
        ? { plan, term: "yearly", endsTrial: false }
        : null;
    // NOVA Pro has no trial, and the Suite is sold without one, so a Suite in
    // a trial is not one this switch was made for.
    case "suite_to_pro":
      return !trialing && plan === "marketing_suite"
        ? { plan: "nova_pro", term: request.term, endsTrial: false }
        : null;
    // Only a paid plan moves down, and on the term it already has. The cancel
    // flow, which offers it, gives a trial no downgrade.
    case "downgrade": {
      const lower = nextPlanDown(plan);
      return !trialing && lower !== null && request.term === term
        ? { plan: lower, term, endsTrial: false }
        : null;
    }
    default: {
      const exhaustive: never = request.mode;
      throw new Error(`Unknown plan change: ${String(exhaustive)}`);
    }
  }
}

/** The call to action on one plan card of Plans and Pricing. */
export type PlanCardCta =
  /** No plan the app can change: the card keeps its checkout or billing link. */
  | { kind: "offer" }
  /** Opens the confirm page for this change. */
  | {
      kind: "change";
      label:
        | "Continue after your trial"
        | "Switch to yearly"
        | "Upgrade to Pro+"
        | typeof SWITCH_TO_PRO_CTA;
      mode: PlanChangeMode;
      term: BillingTerm;
    }
  /** A plan she already has. Not clickable. */
  | {
      kind: "held";
      label:
        | typeof OWN_PLAN_LABEL
        | typeof FREE_ACCESS_PLAN_CTA
        | "Included in your plan";
    }
  /** The card's checkout, at this term, carries her free win-back week. */
  | {
      kind: "winback_trial";
      label: typeof WINBACK_TRIAL_CTA;
      term: BillingTerm;
      /** She was last on NOVA Pro, so the card says she can upgrade to it
       *  from the trial. */
      lastOnPro: boolean;
    };

export interface PlanCardFacts {
  lifecycle: CoachLifecycle;
  /**
   * Her live subscription's plan and term, when the app can change it (see
   * {@link subscriptionRefusal}). Null otherwise.
   */
  current: { plan: PlanKey; term: BillingTerm } | null;
  /**
   * The term of her standalone Marketing Suite subscription, when she has no
   * NOVA plan and the server would switch it to NOVA Pro now
   * (`readSuiteSwitch`). Null otherwise.
   */
  suite: { term: BillingTerm } | null;
  /** Every plan she pays for, on this subscription or on another one. */
  subscribedPlans: ReadonlySet<PlanKey>;
  /** The term this card shows (`shownTerm`): the page's toggle, or the card's
   *  only priced term. */
  selectedTerm: BillingTerm;
  /** The terms this card has a price for. The server refuses a change onto any
   *  other term as `price_unavailable`. */
  pricedTerms: ReadonlySet<BillingTerm>;
  /** A lapsed coach's way back (`readNovaWinbackPlans`). Null for anyone else,
   *  and whenever it could not be read. */
  winback: WinbackPlans | null;
  /** She holds NOVA Core through free access from staff, with no Stripe plan
   *  giving her NOVA. */
  freeAccess: boolean;
}

/** The facts the server reads for the plan cards (`readPlanCards`). The page
 *  adds the term on screen, the terms each card is priced on and the plans she
 *  pays for. */
export type PlanCardServerFacts = Omit<
  PlanCardFacts,
  "selectedTerm" | "pricedTerms" | "subscribedPlans"
>;

/** The change a higher plan's card offers: its label and mode. */
const UPGRADE_CTA = {
  nova_pro: { label: SWITCH_TO_PRO_CTA, mode: "upgrade_pro" },
  nova_pro_plus: { label: "Upgrade to Pro+", mode: "upgrade_pro_plus" },
} as const satisfies Partial<
  Record<
    ChangeablePlan,
    Pick<Extract<PlanCardCta, { kind: "change" }>, "label" | "mode">
  >
>;

/**
 * What a plan card offers, from her plan, its term, the term on screen, her
 * lifecycle and the other plans she pays for. Her own card is clickable only
 * to change how she pays: from the trial to paid, or from monthly to yearly.
 * A higher plan's card upgrades her to it.
 *
 * The NOVA Pro+ card is built only for an Accelerator graduate (the page is
 * sent Pro+ only for her), so its CTA is worked out as for any other plan.
 *
 * A term on screen she cannot move to (monthly while she pays yearly) is read
 * as her own term, so the card never offers a change the server refuses. For
 * the same reason a card offers no change onto a term it has no price for.
 *
 * A lapsed coach's free week sits on one card at one term, the one the
 * checkout gives it on. On the other term the card is an ordinary offer.
 *
 * A coach who pays for the Marketing Suite alone has no NOVA plan, and her
 * NOVA Pro card switches the Suite to it, on the Suite's term or up to yearly.
 */
export function planCardCta(
  card: ChangeablePlan,
  {
    lifecycle,
    current,
    subscribedPlans,
    selectedTerm,
    pricedTerms,
    winback,
    freeAccess,
    suite,
  }: PlanCardFacts,
): PlanCardCta {
  if (current === null && suite !== null && card === "nova_pro") {
    const term = canMoveTerm(suite.term, selectedTerm)
      ? selectedTerm
      : suite.term;
    // Anything else she pays for that NOVA Pro contains would be billed twice.
    const overlaps = conflictingPlanKeys(card).some(
      (plan) => plan !== "marketing_suite" && subscribedPlans.has(plan),
    );
    return overlaps || !pricedTerms.has(term)
      ? { kind: "offer" }
      : {
          kind: "change",
          label: SWITCH_TO_PRO_CTA,
          mode: "suite_to_pro",
          term,
        };
  }
  if (current === null) {
    if (freeAccess && card === "nova")
      return { kind: "held", label: FREE_ACCESS_PLAN_CTA };
    const trial = winback?.trial ?? null;
    return lifecycle === "churned" &&
      trial?.plan === card &&
      trial.term === selectedTerm
      ? {
          kind: "winback_trial",
          label: WINBACK_TRIAL_CTA,
          term: selectedTerm,
          lastOnPro: winback?.lastPlan?.plan === "nova_pro",
        }
      : { kind: "offer" };
  }
  if (lifecycle !== "trial" && lifecycle !== "paid") return { kind: "offer" };
  if (PLAN_RANK[current.plan] < PLAN_RANK.nova) return { kind: "offer" };

  const term = canMoveTerm(current.term, selectedTerm)
    ? selectedTerm
    : current.term;

  if (PLAN_RANK[card] < PLAN_RANK[current.plan])
    return { kind: "held", label: "Included in your plan" };

  // The server charges the catalogue's price for any change except starting
  // to pay on her own plan and term, and refuses a term with no price.
  const keepsPrice = card === current.plan && term === current.term;
  if (!keepsPrice && !pricedTerms.has(term)) return { kind: "offer" };

  if (card === current.plan) {
    if (lifecycle === "trial")
      return {
        kind: "change",
        label: "Continue after your trial",
        mode: "start_paid",
        term,
      };
    return current.term === "monthly" && term === "yearly"
      ? {
          kind: "change",
          label: "Switch to yearly",
          mode: "switch_yearly",
          term,
        }
      : { kind: "held", label: OWN_PLAN_LABEL };
  }

  // A higher plan than hers. Only Pro and Pro+ are above another NOVA plan.
  if (card === "nova") return { kind: "offer" };
  // A separate plan that the bundle also contains, such as a standalone
  // Marketing Suite, would be billed twice after the upgrade. The row then
  // keeps its overlap notice and its way to billing, and the server refuses
  // the same upgrade.
  const overlaps = conflictingPlanKeys(card).some(
    (plan) => plan !== current.plan && subscribedPlans.has(plan),
  );
  return overlaps
    ? { kind: "offer" }
    : { kind: "change", ...UPGRADE_CTA[card], term };
}

/** What a plan card's button does for her. Each card has at most one. */
export type PlanCardAction =
  /** A change to her plan, on the confirm page. */
  | { kind: "change"; mode: PlanChangeMode }
  /** Her own plan: a badge, no button. */
  | { kind: "held" }
  /** NOVA Core, past due: the way to fix her card. */
  | { kind: "update_payment" }
  /** NOVA Core, free access from staff: a badge, no button. */
  | { kind: "free_access" }
  /** NOVA Core, with no NOVA access: its trial, or its subscribe button. */
  | { kind: "start_core" }
  /** NOVA Core, with NOVA access: a Trial or Active badge, no button. */
  | { kind: "access_status" }
  /** A plan she pays for on its own subscription: a badge, no button. */
  | { kind: "owned" }
  /** Checkout is blocked: the card explains why and sends her to billing. */
  | { kind: "manage" }
  /** Checkout of a plan she can buy. */
  | { kind: "subscribe" }
  | { kind: "none" };

/** What one plan card knows about her. */
export interface PlanCardActionFacts {
  plan: PlanKey;
  cta: PlanCardCta | undefined;
  novaAccess: NovaAccessState;
  hasExistingNovaSubscription: boolean;
  /** She pays for this plan. */
  subscribed: boolean;
  /** A plan she pays for whose entitlements this one would duplicate. */
  conflictsWith: PlanKey | undefined;
  /**
   * Stripe holds her NOVA plan unpaid. Her access reads as none, and a second
   * subscription is refused, so her NOVA Core card sends her to billing.
   */
  suspended: boolean;
  /** The card has a price that checkout can sell ({@link sellsAtCheckout}). */
  sellsAtCheckout: boolean;
}

/**
 * Whether checkout sells this plan at this price: NOVA Pro+ through its own
 * checkout, the other plans when the price carries a catalogue lookup key.
 * `option` is the price the card shows, or null for a plan with none.
 */
export function sellsAtCheckout(
  plan: PlanKey,
  option: { lookupKey: string } | null,
): boolean {
  return (
    option !== null &&
    (plan === "nova_pro_plus" || isSuitePlanLookupKey(option.lookupKey))
  );
}

/**
 * What a plan card's button does, from her plan and what she pays for. A card
 * that would bill her twice for what she holds sends her to billing instead of
 * to checkout. Free access from staff blocks no other plan, because it has no
 * subscription behind it.
 */
export function planCardAction({
  plan,
  cta,
  novaAccess,
  hasExistingNovaSubscription,
  subscribed,
  conflictsWith,
  suspended,
  sellsAtCheckout: sells,
}: PlanCardActionFacts): PlanCardAction {
  if (cta?.kind === "change") return { kind: "change", mode: cta.mode };
  if (cta?.kind === "held") return { kind: "held" };
  if (plan === "nova") {
    switch (novaAccess) {
      case "past_due":
        return { kind: "update_payment" };
      case "free_access":
        return { kind: "free_access" };
      case "none":
        return suspended ? { kind: "update_payment" } : { kind: "start_core" };
      case "subscribed":
      case "trialing":
        return { kind: "access_status" };
      default: {
        const exhaustive: never = novaAccess;
        throw new Error(`Unknown NOVA access: ${String(exhaustive)}`);
      }
    }
  }
  if (subscribed) return { kind: "owned" };
  const holdsNovaSubscription =
    hasExistingNovaSubscription ||
    (novaAccess !== "none" && novaAccess !== "free_access");
  const bundle = plan === "nova_pro" || plan === "nova_pro_plus";
  if (conflictsWith || (bundle && holdsNovaSubscription))
    return { kind: "manage" };
  return sells ? { kind: "subscribe" } : { kind: "none" };
}

/** The one thing the page asks her to do next. */
export type NextStep =
  /** The NOVA Core card's Update payment method. */
  | { kind: "update_payment" }
  /** Keep NOVA, or Keep my trial, in the cancel section. */
  | { kind: "keep_plan" }
  /** The button on this plan's card. */
  | { kind: "plan"; plan: ChangeablePlan };

/**
 * Her next step: the one purple button on Plans and Pricing, or null when
 * nothing is left to sell her. The first that applies wins:
 *
 * 1. Her payment failed, or Stripe holds her plan unpaid: update the payment
 *    method.
 * 2. Her NOVA plan or trial is set to end: keep it.
 * 3. NOVA Pro+ is on offer to her: its button.
 * 4. She has no NOVA access: NOVA Core, with its trial when she can still take
 *    one, including the free win-back week, or else its subscribe button.
 * 5. NOVA Pro is a step up from her plan, or from free access: its button.
 *
 * Her own plan's change (continue after the trial, switch to yearly) and the
 * switch of a standalone Marketing Suite to NOVA Pro are never the next step.
 */
export function nextStep({
  cancelScheduled,
  actions,
}: {
  cancelScheduled: boolean;
  /** What each plan card's button does, for the cards the page shows. */
  actions: Partial<Record<ChangeablePlan, PlanCardAction>>;
}): NextStep | null {
  if (actions.nova?.kind === "update_payment")
    return { kind: "update_payment" };
  if (cancelScheduled) return { kind: "keep_plan" };
  if (movesOnto(actions.nova_pro_plus, "upgrade_pro_plus"))
    return { kind: "plan", plan: "nova_pro_plus" };
  if (actions.nova?.kind === "start_core")
    return { kind: "plan", plan: "nova" };
  if (movesOnto(actions.nova_pro, "upgrade_pro"))
    return { kind: "plan", plan: "nova_pro" };
  return null;
}

/** A button that buys the plan, or upgrades her to it from a lower one. */
function movesOnto(
  action: PlanCardAction | undefined,
  upgrade: PlanChangeMode,
): boolean {
  return (
    action?.kind === "subscribe" ||
    (action?.kind === "change" && action.mode === upgrade)
  );
}

/** Whether the next step is the button on this plan's card. */
export function isNextStepCard(step: NextStep | null, plan: PlanKey): boolean {
  if (step === null) return false;
  if (step.kind === "update_payment") return plan === "nova";
  return step.kind === "plan" && step.plan === plan;
}
